import axios from 'axios';
import { refreshToken } from './services/refresh-token';
import { handleApiError } from './handle-api-error';
import { callLogout } from './auth-logout-callback';
import { isOnline } from 'src/utils';

const api = axios.create({
  baseURL: import.meta.env.VITE_API_URL,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Attach access token to all requests
api.interceptors.request.use(config => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

let isRefreshing = false;
let refreshQueue = [];

// Retry all pending requests after token refresh
function processQueue(error, token = null) {
  refreshQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  refreshQueue = [];
}

api.interceptors.response.use(
  response => response,
  async error => {
    const originalRequest = error.config;

    if (originalRequest && originalRequest.url && originalRequest.url.includes('/auth/refresh')) {
      return Promise.reject(handleApiError(error));
    }

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      if (isRefreshing) {
        // Wait for the refresh to finish
        return new Promise((resolve, reject) => {
          refreshQueue.push({ resolve, reject });
        }).then(newToken => {
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
          return api(originalRequest);
        }).catch(err => {
          return Promise.reject(err);
        });
      }

      isRefreshing = true;

      try {
        const newToken = await refreshToken();
        if (!newToken) throw new Error('No new token');

        localStorage.setItem('token', newToken); // update localStorage
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        processQueue(null, newToken); // resolve all queued requests
        return api(originalRequest);
      } catch (refreshErr) {
        processQueue(refreshErr, null);

        if (isOnline()) {
          console.log('Token refresh failed while online → logging out');
          callLogout();
        } else {
          console.log('Token refresh failed but we are OFFLINE → staying logged in');
        }

        return Promise.reject(refreshErr);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(handleApiError(error));
  }
);

export default api;
